import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { useUtilityTargets } from "@/shared/selectors/UtilityTargetSelector";
import { getMomentTz } from "@dexteel/mesf-core";
import type { GridApi } from "ag-grid-community";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useState, type ReactNode } from "react";
import { useConsumptionReportPeriod } from "../hooks/useConsumptionReportPeriod";
import { useUtilityConsumptionReport } from "../hooks/useUtilityConsumptionReport";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UTILITY_CONSUMPTION_REPORT_ROW_KIND,
	type UtilityConsumptionMeter,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";
import { UTILITY_KIND, type UtilityKind } from "../../models/Utility";
import { UtilityMessage, WipState } from "../../components/UtilityStates";
import {
	getUtilityConsumptionReportBucketLabels,
	getUtilityConsumptionReportPeriodLabel,
	isUtilityConsumptionNextDisabled,
} from "../utils/calendar";
import {
	buildConsumptionReportExportFooter,
	buildConsumptionReportExportHeader,
	getConsumptionReportExportColumnWidth,
	getConsumptionReportExportFileName,
	getConsumptionReportExportPeriodLabel,
	getConsumptionReportExportRangeKey,
	REPORT_MODE_EXPORT_LABEL,
	UTILITY_EXPORT_LABEL,
} from "../utils/reportExport";
import {
	GAS_RATIO_UNIT,
	GAS_UNIT,
	RATIO_UNIT,
	VOLUME_UNIT,
} from "../utils/format";
import {
	createConsumptionReportGridRow,
	type ConsumptionReportGridRow,
} from "../utils/reportGrid";
import { ConsumptionFilterBar } from "./ConsumptionFilterBar";
import type { ConsumptionMeterTreeLabels } from "./ConsumptionMeterTree";
import { ConsumptionReportControls } from "./ConsumptionReportControls";
import { ConsumptionReportGrid } from "./ConsumptionReportGrid";
import { ConsumptionError } from "./ConsumptionStates";

const moment = getMomentTz();

const rowHeaders: Partial<Record<UtilityConsumptionReportMode, string>> = {
	day: "Hour",
	year: "Month",
};
const totalHeaders: Partial<Record<UtilityConsumptionReportMode, string>> = {
	day: "Hourly Total",
	year: "Monthly Total",
	section: "Event Total",
};

const reportUnits: Partial<
	Record<UtilityKind, { unit: string; ratioUnit: string }>
> = {
	[UTILITY_KIND.WATER]: { unit: VOLUME_UNIT, ratioUnit: RATIO_UNIT },
	[UTILITY_KIND.GAS]: { unit: GAS_UNIT, ratioUnit: GAS_RATIO_UNIT },
};

interface ConsumptionReportProps {
	targetId: number | null;
	utility: UtilityKind;
	meters: UtilityConsumptionMeter[];
	isMeterLoading: boolean;
	selectedMeterIds: string[] | null;
	resolvedMeterIds: string[] | null;
	areMetersLoaded: boolean;
	isEmptySelection: boolean;
	range: UtilityConsumptionReportRange;
	areaSelector?: ReactNode;
	meterLabels?: ConsumptionMeterTreeLabels;
	getMeterItemLabel?: (meter: UtilityConsumptionMeter) => string;
	showCost: boolean;
	showRatio?: boolean;
	showSection?: boolean;
	onMeterChange: (meterIds: string[] | null) => void;
	onRangeChange: (range: UtilityConsumptionReportRange) => void;
}

export const ConsumptionReport = ({
	targetId,
	utility,
	meters,
	isMeterLoading,
	selectedMeterIds,
	resolvedMeterIds,
	areMetersLoaded,
	isEmptySelection,
	range,
	areaSelector,
	meterLabels,
	getMeterItemLabel,
	showCost,
	showRatio,
	showSection,
	onMeterChange,
	onRangeChange,
}: ConsumptionReportProps) => {
	const [gridApi, setGridApi] =
		useState<GridApi<ConsumptionReportGridRow> | null>(null);
	const [sectionId, setSectionId] = useQueryState("section", parseAsInteger);
	const { data: targets } = useUtilityTargets();
	const reportPeriod = useConsumptionReportPeriod({
		range,
		onRangeChange,
	});
	const { mode, isSection } = reportPeriod;

	const millNames = [...new Set(meters.map((meter) => meter.MillName))];
	const units = reportUnits[utility];
	const itemName = (meterLabels?.items ?? "Meters")
		.toLowerCase()
		.replace(/s$/, "");
	const canSection = showSection !== false;

	const {
		data,
		period,
		isError,
		error,
		isLoading,
		dateError,
		isFetching,
		refetch,
	} = useUtilityConsumptionReport({
		targetId,
		utility,
		externalMeterIds: resolvedMeterIds,
		areMetersLoaded: areMetersLoaded && !isEmptySelection,
		mode,
		sectionId,
		referenceDate: reportPeriod.referenceDate,
		sectionEndBoundary: reportPeriod.sectionEndBoundary,
		range,
		sectionRange: reportPeriod.sectionRange,
	});

	const reportRows = data?.Rows ?? [];
	const reportMeterIds = data?.MeterIds ?? [];

	const shownMillNames = new Set(
		meters
			.filter((meter) => reportMeterIds.includes(meter.ExternalMeterId))
			.map((meter) => meter.MillName),
	);
	const periodLabels = isSection
		? []
		: getUtilityConsumptionReportBucketLabels(
				reportRows.map((row) => row.PeriodStartUtc),
				mode,
			);
	const formatEventTime = (value: Date) =>
		moment(value).format("MM/DD/YYYY HH:mm");

	const rows: ConsumptionReportGridRow[] = reportRows.map((row, index) => {
		const values = {
			valuePerTon: row.ValuePerTon,
			valuePerBillet: row.ValuePerBillet,
			meterIds: reportMeterIds,
			meterValues: row.MeterValue,
			meterCoverage: row.MeterCoverage,
			meterPartial: row.MeterPartial,
			value: row.PeriodValue,
			costPerTon: row.CostPerTon,
			cost: row.PeriodCost,
			isCostApproximate: row.IsCostApproximate,
			isValuePartial: row.IsValuePartial,
			isValueApproximate: row.IsValueApproximate,
			isCostPartial: row.IsCostPartial,
		};

		return row.Kind === UTILITY_CONSUMPTION_REPORT_ROW_KIND.CALENDAR
			? createConsumptionReportGridRow({
					...values,
					id: row.PeriodStartUtc.toISOString(),
					period: periodLabels[index],
				})
			: createConsumptionReportGridRow({
					...values,
					id: `event:${row.MillAssetId}:${row.EventId}`,
					mill: row.MillName,
					section: row.SectionName,
					event: `#${row.EventId}`,
					eventStart: formatEventTime(row.EventStartUtc),
					eventEnd: row.EventEndUtc ? formatEventTime(row.EventEndUtc) : "Open",
				});
	});

	const exportDisabled =
		isFetching ||
		isError ||
		dateError !== null ||
		units === undefined ||
		rows.length === 0 ||
		gridApi === null;

	const utilityLabel = UTILITY_EXPORT_LABEL[utility];
	const areaName =
		(targets ?? []).find((target) => target.TargetId === targetId)
			?.TargetName ?? null;
	const hasPartialCells = (data?.Rows ?? []).some((row) =>
		Object.values(row.MeterCoverage).some(
			(coverage) => coverage !== null && coverage < 100,
		),
	);
	const hasPartialTotals = rows.some((row) => row.missingMeters !== "");
	const isPeriodInProgress =
		!isSection &&
		mode !== UTILITY_CONSUMPTION_REPORT_MODE.PERIOD &&
		period !== null &&
		isUtilityConsumptionNextDisabled(period);

	const exportContent = {
		title: `${utilityLabel} consumption report`,
		lines: [
			["Area", areaName ?? "Whole plant"],
			["View", REPORT_MODE_EXPORT_LABEL[mode]],
			[
				"Period",
				getConsumptionReportExportPeriodLabel({
					calendarLabel:
						reportPeriod.calendarMode === null
							? null
							: getUtilityConsumptionReportPeriodLabel(
									reportPeriod.calendarMode,
									reportPeriod.referenceDate,
								),
					isSection,
					activeRange: reportPeriod.activeRange,
				}),
			],
			[
				`${itemName.charAt(0).toUpperCase()}${itemName.slice(1)}s`,
				resolvedMeterIds === null
					? `All (${meters.length})`
					: `${resolvedMeterIds.length} of ${meters.length}`,
			],
			["Figures in", units === undefined ? "" : units.unit],
			["Exported", moment().format("MM/DD/YYYY HH:mm")],
		] satisfies [string, string][],
		notes: [
			...(hasPartialCells
				? [
						`Shaded: the ${itemName} reported only part of that period, so the figure is what it measured, not what went through it.`,
					]
				: []),
			...(hasPartialTotals
				? [
						`A row where a ${itemName} said nothing at all adds up only the ones that did report.`,
					]
				: []),
			...(isPeriodInProgress
				? [
						"The last row is still being measured, so its figures cover only the hours reported so far.",
					]
				: []),
			...(showCost && rows.some((row) => row.isCostApproximate)
				? [
						"Where no bill covers a period yet, its cost is priced with the previous bill's rate.",
					]
				: []),
			...(data?.IsValuePartial || data?.IsCostPartial
				? [
						"Some figures leave out the hours no bill covers yet, so they come up short.",
					]
				: []),
		],
	};

	const exportToExcel = () => {
		if (exportDisabled || gridApi === null) return;
		gridApi.exportDataAsExcel({
			fileName: getConsumptionReportExportFileName({
				utility,
				areaName,
				rangeKey: getConsumptionReportExportRangeKey({
					mode,
					isSection,
					range,
					sectionRange: reportPeriod.sectionRange,
					period,
				}),
			}),
			sheetName: `${utilityLabel} report`.slice(0, 31),
			author: "Dexteel MESF",
			skipColumnGroupHeaders: false,
			skipPinnedBottom: false,
			freezeColumns: ({ column }) => !column.getColId().startsWith("meter:"),
			columnWidth: ({ column }) =>
				getConsumptionReportExportColumnWidth(column?.getColId() ?? ""),
			prependContent: buildConsumptionReportExportHeader(exportContent),
			appendContent: buildConsumptionReportExportFooter(exportContent),
		});
	};
	useEffect(() => {
		setSectionId(null);
	}, [targetId]);

	useEffect(() => {
		if (isSection && !canSection) {
			reportPeriod.changeMode(UTILITY_CONSUMPTION_REPORT_MODE.PERIOD);
		}
	}, [canSection, isSection]);

	return (
		<>
			<ConsumptionFilterBar
				meters={meters}
				isMeterLoading={isMeterLoading}
				selectedMeterIds={selectedMeterIds}
				meterLabels={meterLabels}
				getMeterItemLabel={getMeterItemLabel}
				areaSelector={areaSelector}
				onMeterChange={onMeterChange}
				actions={
					<ExcelButton
						handleExportToExcel={exportToExcel}
						disabled={exportDisabled}
					/>
				}
			>
				<ConsumptionReportControls
					period={reportPeriod}
					showSection={canSection}
					sectionId={sectionId}
					millNames={millNames}
					nextDisabled={
						period === null || isUtilityConsumptionNextDisabled(period)
					}
					dateError={dateError}
					onSectionChange={setSectionId}
				/>
			</ConsumptionFilterBar>
			{units === undefined ? (
				<WipState utility={utility} />
			) : isEmptySelection ? (
				<UtilityMessage
					title={`No ${itemName}s selected`}
					detail={`Tick at least one ${itemName} in the filter to see the report.`}
				/>
			) : (
				<>
					{isError && (
						<ConsumptionError
							message={
								error instanceof Error ? error.message : "Unexpected response."
							}
							onRetry={() => refetch()}
						/>
					)}
					<ConsumptionReportGrid
						rows={rows}
						meterIds={reportMeterIds}
						meters={meters}
						meterTotalValue={data?.MeterTotalValue ?? {}}
						meterTotalPartial={data?.MeterTotalPartial ?? {}}
						grandTotalValue={data?.GrandTotalValue ?? null}
						grandValuePerTon={data?.GrandValuePerTon ?? null}
						grandValuePerBillet={data?.GrandValuePerBillet ?? null}
						grandTotalCost={data?.GrandTotalCost ?? null}
						grandCostPerTon={data?.GrandCostPerTon ?? null}
						isCostApproximate={data?.IsCostApproximate ?? false}
						isValuePartial={data?.IsValuePartial ?? false}
						isValueApproximate={data?.IsValueApproximate ?? false}
						isCostPartial={data?.IsCostPartial ?? false}
						showCost={showCost}
						showRatio={showRatio}
						isSection={isSection}
						showMill={shownMillNames.size > 1}
						periodHeader={rowHeaders[mode] ?? "Date"}
						totalHeader={totalHeaders[mode] ?? "Daily Total"}
						getMeterItemLabel={getMeterItemLabel}
						itemName={itemName}
						unit={units.unit}
						ratioUnit={units.ratioUnit}
						isLoading={isFetching}
						onGridReady={setGridApi}
					/>
				</>
			)}
		</>
	);
};
